import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  ContextService,
  SessionService,
  TokenService,
} from '@maximizer/core/shared/data-access';
import { Session } from '@maximizer/core/shared/domain';
import { isNullOrEmpty } from '@maximizer/shared/util';
import { catchError, firstValueFrom, map, Observable, of, switchMap } from 'rxjs';
import { IQChat, IQChatAddon, IQChatAttachment, IQChatFrom, IQChatStep } from '../classes/iqBoost';
import {
  DEFAULT_LANGGRAPH_CONFIG,
  LANGGRAPH_CONFIG,
  LangGraphConfig,
} from '../types/injection-token';
import {
  LangGraphAssistantId,
  LangGraphConfirmation,
  LangGraphConfirmationAnswer,
  LangGraphConversation,
  LangGraphHandoff,
  LangGraphMessageRole,
  LangGraphOutputMessage,
  LangGraphQuestionMetadata,
  LangGraphRunOptions,
  LangGraphStreamUpdate,
  LangGraphThread,
  LangGraphThreadMetadata,
  LangGraphThreadScope,
  LangGraphToolCallRef,
  LangGraphWriteAction,
  WRITE_CONFIRMATION_TYPE,
} from '../types/langgraph.types';
import { LangGraphSseEvent, parseLangGraphServerSentEvents } from './langgraph-sse';
import { LangGraphUpload } from './langgraph-upload';

interface LangGraphInputMessage {
  role: LangGraphMessageRole;
  content: string;
  response_metadata?: LangGraphQuestionMetadata;
}

interface LangGraphRunInput {
  messages: LangGraphInputMessage[];
  // The thread keeps every file it was sent: sending one again adds a second copy.
  uploads?: LangGraphUpload[];
  model?: string;
}

interface LangGraphRunStreamRequest {
  assistant_id: LangGraphAssistantId;
  input?: LangGraphRunInput;
  // Resumes the run the thread stopped on, in place of a new question.
  command?: { resume: LangGraphConfirmationAnswer };
  stream_mode: ('messages-tuple' | 'custom')[];
  // Kept by the server, so a client that left can join the run again and replay it from the start.
  stream_resumable: boolean;
}

interface LangGraphThreadState {
  values?: { messages?: LangGraphOutputMessage[]; [key: string]: unknown } | null;
  // A thread whose run stopped to ask holds the question as the interrupt of its next task.
  tasks?: { interrupts?: { value?: unknown }[] | null }[] | null;
  [key: string]: unknown;
}

interface LangGraphToolCall {
  id?: string | null;
  name?: string | null;
  args?: unknown;
}

interface LangGraphMessageChunk extends Omit<LangGraphOutputMessage, 'type'> {
  type: LangGraphMessageRole | 'AIMessageChunk';
  tool_calls?: LangGraphToolCall[];
  tool_call_chunks?: LangGraphToolCall[];
  tool_call_id?: string;
}

// The thread's values would carry the whole conversation.
const THREAD_SUMMARY_FIELDS = ['thread_id', 'updated_at', 'state_updated_at', 'metadata', 'status'];

const THREAD_SEARCH_LIMIT = 50;

@Injectable()
export class LangGraphService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(SessionService);
  private readonly context = inject(ContextService);
  private readonly token = inject(TokenService);
  private readonly config: LangGraphConfig =
    inject(LANGGRAPH_CONFIG, { optional: true }) ?? DEFAULT_LANGGRAPH_CONFIG;

  createThread(title: string, scope: LangGraphThreadScope): Observable<string> {
    return this.session.getInfo().pipe(
      switchMap((session) => {
        const metadata: LangGraphThreadMetadata = {
          ...this.ownerMetadata(session),
          ...scope,
          title,
        };
        return this.http.post<LangGraphThread>(
          `${this.config.url}/threads`,
          { metadata },
          { headers: this.getHeaders() },
        );
      }),
      map((thread) => thread.thread_id),
    );
  }

  searchThreads(
    scope: LangGraphThreadScope,
    limit = THREAD_SEARCH_LIMIT,
  ): Observable<LangGraphThread[]> {
    return this.search(scope, {}, limit);
  }

  // Asked for on their own, so a pin too old to be among the recent threads still shows.
  searchPinnedThreads(
    scope: LangGraphThreadScope,
    limit = THREAD_SEARCH_LIMIT,
  ): Observable<LangGraphThread[]> {
    return this.search(scope, { pinned: true }, limit);
  }

  getThreadConversation(threadId: string): Observable<LangGraphConversation> {
    return this.session.getInfo().pipe(
      switchMap(() =>
        this.http.get<LangGraphThreadState>(`${this.threadUrl(threadId)}/state`, {
          headers: this.getHeaders(),
        }),
      ),
      map((state) => ({
        messages: state?.values?.messages ?? [],
        handoffs: readHandoffs(state?.values?.['handoffs']),
        confirmations: [
          ...readConfirmations(state?.values?.['confirmations']),
          ...readConfirmations(
            (state?.tasks ?? []).flatMap((task) => (task?.interrupts ?? []).map((i) => i?.value)),
          ),
        ],
      })),
    );
  }

  renameThread(threadId: string, title: string): Observable<void> {
    return this.patchMetadata(threadId, { title });
  }

  pinThread(threadId: string, pinnedAt: string): Observable<void> {
    return this.patchMetadata(threadId, { pinned: true, pinned_at: pinnedAt });
  }

  // A merge cannot drop a key, so the pin is turned off rather than removed.
  unpinThread(threadId: string): Observable<void> {
    return this.patchMetadata(threadId, { pinned: false });
  }

  readThreadMetadata(threadId: string): Observable<Record<string, unknown> | null> {
    return this.session.getInfo().pipe(
      switchMap((session) =>
        this.http.post<LangGraphThread[]>(
          `${this.config.url}/threads/search`,
          {
            ids: [threadId],
            metadata: this.ownerMetadata(session),
            limit: 1,
            select: ['thread_id', 'metadata'],
          },
          { headers: this.getHeaders() },
        ),
      ),
      // A server that does not know `ids` answers with another thread: only this one counts.
      map((threads) => threads.find((thread) => thread.thread_id === threadId)?.metadata ?? null),
    );
  }

  // A shallow merge: a key left out keeps its value, and a key given is replaced whole.
  updateThreadMetadata(threadId: string, metadata: Record<string, unknown>): Observable<void> {
    return this.patchMetadata(threadId, metadata);
  }

  // Runs outlive the client that started them, so the thread's run is stopped before it goes.
  // Best effort: a run that cannot be stopped does not keep the thread.
  deleteThread(threadId: string): Observable<void> {
    return this.findActiveRun(threadId).pipe(
      switchMap((runId) => (runId ? this.cancelRun(threadId, runId) : of(undefined))),
      catchError(() => of(undefined)),
      switchMap(() =>
        this.http.delete<void>(this.threadUrl(threadId), { headers: this.getHeaders() }),
      ),
      map(() => undefined),
    );
  }

  /**
   * Streams a run on the thread. The content is the only message sent, so what scopes the
   * orchestrator's lookups rides in its text. Unsubscribing aborts the request.
   */
  runStream(
    content: string,
    threadId: string,
    options: LangGraphRunOptions = {},
  ): Observable<LangGraphStreamUpdate> {
    const body: LangGraphRunStreamRequest = {
      assistant_id: options.assistantId ?? this.config.assistantId,
      input: this.buildInput(content, options),
      stream_mode: ['messages-tuple', 'custom'],
      stream_resumable: true,
    };

    return this.session.getInfo().pipe(
      switchMap(() =>
        this.openStream(`${this.threadUrl(threadId)}/runs/stream`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      ),
    );
  }

  // Answers the confirmation the thread's run stopped on; the run goes on from where it stopped.
  resumeStream(
    threadId: string,
    answer: LangGraphConfirmationAnswer,
    assistantId: LangGraphAssistantId = this.config.assistantId,
  ): Observable<LangGraphStreamUpdate> {
    const body: LangGraphRunStreamRequest = {
      assistant_id: assistantId,
      command: { resume: answer },
      stream_mode: ['messages-tuple', 'custom'],
      stream_resumable: true,
    };

    return this.session.getInfo().pipe(
      switchMap(() =>
        this.openStream(`${this.threadUrl(threadId)}/runs/stream`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      ),
    );
  }

  findActiveRun(threadId: string): Observable<string | null> {
    return this.session.getInfo().pipe(
      switchMap(() =>
        this.http.get<{ run_id: string }[]>(`${this.threadUrl(threadId)}/runs`, {
          headers: this.getHeaders(),
          params: { status: 'running', limit: 1, select: 'run_id' },
        }),
      ),
      map((runs) => runs[0]?.run_id ?? null),
    );
  }

  // The run's updates from its first, as `runStream` emitted them, then as it goes on.
  joinRunStream(threadId: string, runId: string): Observable<LangGraphStreamUpdate> {
    return this.session.getInfo().pipe(
      switchMap(() =>
        this.openStream(`${this.threadUrl(threadId)}/runs/${encodeURIComponent(runId)}/stream`, {
          method: 'GET',
          headers: { 'Last-Event-ID': '-1' },
        }),
      ),
    );
  }

  cancelRun(threadId: string, runId: string): Observable<void> {
    return this.session.getInfo().pipe(
      switchMap(() =>
        this.http.post<void>(
          `${this.threadUrl(threadId)}/runs/${encodeURIComponent(runId)}/cancel`,
          null,
          { headers: this.getHeaders(), params: { wait: true } },
        ),
      ),
      map(() => undefined),
    );
  }

  private buildInput(
    content: string,
    {
      uploads = [],
      attachments = uploads,
      question,
      addon,
      model,
    }: LangGraphRunOptions,
  ): LangGraphRunInput {
    const metadata = readQuestionMetadata(content, attachments, question, addon);
    return {
      messages: [{ role: 'human', content, ...(metadata ? { response_metadata: metadata } : {}) }],
      ...(uploads.length > 0 ? { uploads } : {}),
      ...(model ? { model } : {}),
    };
  }

  private openStream(
    url: string,
    { headers, ...init }: Omit<RequestInit, 'headers'> & { headers?: Record<string, string> },
  ): Observable<LangGraphStreamUpdate> {
    return new Observable<LangGraphStreamUpdate>((subscriber) => {
      const controller = new AbortController();
      const open = (bearer: string): Promise<Response> =>
        fetch(url, {
          ...init,
          headers: { ...this.getHeaders(bearer), Accept: 'text/event-stream', ...headers },
          signal: controller.signal,
        });

      const consume = async (): Promise<void> => {
        let response = await open(this.context.token || '');
        if (response.status === 401) {
          response = await open(await this.refreshToken());
        }

        if (!response.ok) {
          throw new Error(`LangGraph stream request failed with status ${response.status}`);
        }
        if (!response.body) {
          throw new Error('LangGraph stream response did not include a body');
        }

        for await (const event of parseLangGraphServerSentEvents(response.body)) {
          const update = toStreamUpdate(event);
          if (update) {
            subscriber.next(update);
          }
        }
      };

      consume()
        .then(() => subscriber.complete())
        .catch((error: unknown) => {
          // Our own teardown aborts the request: that AbortError is no failure.
          if (!controller.signal.aborted) {
            subscriber.error(error);
          }
        });

      return () => controller.abort();
    });
  }

  // Streams go through fetch, out of the interceptor's reach, so they refresh the token themselves.
  private async refreshToken(): Promise<string> {
    this.context.token = '';
    const refreshed = await firstValueFrom(this.token.getToken());
    if (isNullOrEmpty(refreshed)) {
      throw new Error('LangGraph stream request failed: unable to refresh the token');
    }
    return refreshed;
  }

  private search(
    scope: LangGraphThreadScope,
    filter: Partial<LangGraphThreadMetadata>,
    limit: number,
  ): Observable<LangGraphThread[]> {
    return this.session.getInfo().pipe(
      switchMap((session) =>
        this.http.post<LangGraphThread[]>(
          `${this.config.url}/threads/search`,
          {
            metadata: { ...this.ownerMetadata(session), ...scope, ...filter },
            limit,
            sort_by: 'updated_at',
            sort_order: 'desc',
            select: THREAD_SUMMARY_FIELDS,
          },
          { headers: this.getHeaders() },
        ),
      ),
    );
  }

  private patchMetadata(
    threadId: string,
    metadata: Partial<LangGraphThreadMetadata>,
  ): Observable<void> {
    return this.session.getInfo().pipe(
      switchMap(() =>
        this.http.patch<LangGraphThread>(
          this.threadUrl(threadId),
          { metadata },
          { headers: this.getHeaders() },
        ),
      ),
      map(() => undefined),
    );
  }

  private threadUrl(threadId: string): string {
    return `${this.config.url}/threads/${encodeURIComponent(threadId)}`;
  }

  private ownerMetadata(session: Session): Pick<LangGraphThreadMetadata, 'owner_id' | 'database_id'> {
    return {
      owner_id: session.user.id,
      database_id: session.uniqueDatabaseId,
    };
  }

  private getHeaders(bearer = this.context.token || ''): Record<string, string> {
    return {
      Authorization: `Bearer ${bearer}`,
      'Content-Type': 'application/json',
      ...(this.config.apiValue ? { 'x-api-key': this.config.apiValue } : {}),
    };
  }
}

function readTextContent(content: LangGraphOutputMessage['content'] | undefined): string {
  if (typeof content === 'string') {
    return content;
  }
  return (content ?? [])
    .filter((block) => block.type === 'text' && block.text)
    .map((block) => block.text)
    .join('');
}

// Only the files' names stay on the question - the files go as uploads - and the question as
// shown only when the content sent says more.
function readQuestionMetadata(
  content: string,
  files: IQChatAttachment[],
  question: string | undefined,
  addon: IQChatAddon | undefined,
): LangGraphQuestionMetadata | undefined {
  const attachments = files.map(({ name }) => ({ name }));
  const shown = question !== undefined && question !== content ? question : undefined;
  if (attachments.length === 0 && shown === undefined && !addon) {
    return undefined;
  }
  return {
    ...(attachments.length > 0 ? { attachments } : {}),
    ...(shown !== undefined ? { question: shown } : {}),
    ...(addon ? { addon: { override: addon.override, ref: addon.ref } } : {}),
  };
}

function readAddon(message: LangGraphOutputMessage): IQChatAddon | undefined {
  const addon = message.response_metadata?.['addon'] as Partial<IQChatAddon> | null | undefined;
  return typeof addon?.override === 'string' &&
    addon.override.length > 0 &&
    typeof addon.ref === 'string' &&
    addon.ref.length > 0
    ? { override: addon.override, ref: addon.ref }
    : undefined;
}

function readShownQuestion(message: LangGraphOutputMessage): string | undefined {
  const question = message.response_metadata?.['question'];
  return typeof question === 'string' && question.length > 0 ? question : undefined;
}

// A question stored before the names were kept has none: the thread's uploads cannot tell which
// question each file came with.
function readAttachments(message: LangGraphOutputMessage): IQChatAttachment[] {
  const attachments = message.response_metadata?.['attachments'];
  if (!Array.isArray(attachments)) {
    return [];
  }
  return attachments
    .map((attachment: unknown) => (attachment as { name?: unknown } | null)?.name)
    .filter((name): name is string => typeof name === 'string' && name.length > 0)
    .map((name) => ({ name }));
}

function readHandoff(value: unknown): LangGraphHandoff | null {
  const handoff = value as Partial<LangGraphHandoff> | null | undefined;
  const { id, type, version, questionId, createdAt, data } = handoff ?? {};
  if (
    typeof id !== 'string' ||
    typeof type !== 'string' ||
    typeof version !== 'number' ||
    typeof createdAt !== 'string' ||
    typeof data !== 'object' ||
    data === null
  ) {
    return null;
  }
  return {
    id,
    type,
    version,
    questionId: typeof questionId === 'string' ? questionId : null,
    createdAt,
    data,
  };
}

function readHandoffs(value: unknown): LangGraphHandoff[] {
  return Array.isArray(value)
    ? value.map(readHandoff).filter((handoff): handoff is LangGraphHandoff => handoff !== null)
    : [];
}

const CONFIRMATION_STATUSES: ReadonlySet<string> = new Set(['pending', 'confirmed', 'cancelled']);

function readWriteAction(value: unknown): LangGraphWriteAction | null {
  const action = value as Partial<LangGraphWriteAction> | null | undefined;
  const { toolCallId, tool, args } = action ?? {};
  return typeof toolCallId === 'string' &&
    typeof tool === 'string' &&
    typeof args === 'object' &&
    args !== null &&
    !Array.isArray(args)
    ? { toolCallId, tool, args }
    : null;
}

function readConfirmation(value: unknown): LangGraphConfirmation | null {
  const confirmation = value as Partial<LangGraphConfirmation> | null | undefined;
  const { id, type, version, questionId, data } = confirmation ?? {};
  if (
    typeof id !== 'string' ||
    type !== WRITE_CONFIRMATION_TYPE ||
    typeof version !== 'number' ||
    typeof questionId !== 'string' ||
    !CONFIRMATION_STATUSES.has(String(data?.status)) ||
    !Array.isArray(data?.actions)
  ) {
    return null;
  }
  const actions = data.actions.map(readWriteAction);
  if (actions.includes(null)) {
    return null;
  }
  return {
    id,
    type,
    version,
    questionId,
    data: { status: data.status, actions: actions as LangGraphWriteAction[] },
  };
}

function readConfirmations(value: unknown): LangGraphConfirmation[] {
  return Array.isArray(value)
    ? value
        .map(readConfirmation)
        .filter((confirmation): confirmation is LangGraphConfirmation => confirmation !== null)
    : [];
}

// AI messages that call tools showed as reasoning, never as answers, so they are dropped, and so
// are the steps of a question that never got an answer.
export function toChatHistory(
  messages: LangGraphOutputMessage[],
  handoffs: LangGraphHandoff[] = [],
  confirmations: LangGraphConfirmation[] = [],
): IQChat[] {
  const history: IQChat[] = [];
  let steps: IQChatStep[] = [];
  let addon: IQChatAddon | undefined;
  let attached: Attached = NOTHING_ATTACHED;

  for (const message of messages) {
    if (message.type === 'tool') {
      if (message.name) {
        steps.push({ tool: message.name });
      }
      continue;
    }

    if (message.type === 'human') {
      history.push(...unanswered(attached));
      steps = [];
      addon = readAddon(message);
      const asked = (item: { questionId: string | null }) =>
        !!message.id && item.questionId === message.id;
      attached = { handoffs: handoffs.filter(asked), confirmations: confirmations.filter(asked) };
      const question = readQuestion(message);
      if (question) {
        history.push(question);
      }
      continue;
    }

    const answer = readAnswer(message);
    if (answer.length > 0) {
      history.push(toAnswer(answer, steps, addon, attached));
      steps = [];
      addon = undefined;
      attached = NOTHING_ATTACHED;
    }
  }
  history.push(...unanswered(attached));
  return history;
}

interface Attached {
  handoffs: LangGraphHandoff[];
  confirmations: LangGraphConfirmation[];
}

const NOTHING_ATTACHED: Attached = { handoffs: [], confirmations: [] };

// A run cut short once it handed off, or stopped to ask, leaves no answer: they show under an
// empty one.
function unanswered(attached: Attached): IQChat[] {
  return attached.handoffs.length > 0 || attached.confirmations.length > 0
    ? [toAnswer('', [], undefined, attached)]
    : [];
}

function readQuestion(message: LangGraphOutputMessage): IQChat | null {
  const question = readShownQuestion(message) ?? readTextContent(message.content);
  if (question.length === 0) {
    return null;
  }
  const attachments = readAttachments(message);
  return {
    content: question,
    from: IQChatFrom.User,
    ...(attachments.length > 0 ? { attachments } : {}),
  };
}

function readAnswer(message: LangGraphOutputMessage): string {
  const callsTools = (message.tool_calls ?? []).length > 0;
  return message.type === 'ai' && !callsTools ? readTextContent(message.content) : '';
}

function toAnswer(
  content: string,
  steps: IQChatStep[],
  addon: IQChatAddon | undefined,
  { handoffs, confirmations }: Attached,
): IQChat {
  return {
    content,
    from: IQChatFrom.Ai,
    ...(steps.length > 0 ? { steps } : {}),
    ...(addon ? { addon } : {}),
    ...(handoffs.length > 0 ? { handoffs } : {}),
    ...(confirmations.length > 0 ? { confirmations } : {}),
  };
}

export function toStreamUpdate(event: LangGraphSseEvent): LangGraphStreamUpdate | null {
  switch (event.event) {
    case 'end':
      return { kind: 'end' };
    case 'error': {
      const error = event.data as { message?: string; error?: string } | null;
      throw new Error(error?.message ?? error?.error ?? 'LangGraph run failed');
    }
    case 'messages':
      return readMessageTuple(event.data);
    case 'custom': {
      const confirmation = readConfirmation(event.data);
      if (confirmation) {
        return { kind: 'confirmation', confirmation };
      }
      const handoff = readHandoff(event.data);
      return handoff ? { kind: 'handoff', handoff } : null;
    }
    default:
      return null;
  }
}

// The name travels in a call's first chunk only, and a chunk may carry a call both parsed
// (`tool_calls`) and raw (`tool_call_chunks`).
function readToolCalls(calls: LangGraphToolCall[]): LangGraphToolCallRef[] {
  const tools: LangGraphToolCallRef[] = [];
  for (const call of calls) {
    if (!call.name) {
      continue;
    }
    const id = call.id ?? null;
    const known = tools.some((tool) =>
      id === null ? tool.id === null && tool.name === call.name : tool.id === id,
    );
    if (!known) {
      tools.push({ id, name: call.name });
    }
  }
  return tools;
}

function readMessageTuple(data: unknown): LangGraphStreamUpdate | null {
  if (!Array.isArray(data) || data.length === 0) {
    return null;
  }
  const chunk = data[0] as LangGraphMessageChunk | null | undefined;
  if (!chunk) {
    return null;
  }

  if (chunk.type === 'tool') {
    return { kind: 'toolResult', toolName: chunk.name ?? '', toolCallId: chunk.tool_call_id ?? null };
  }
  if (chunk.type !== 'ai' && chunk.type !== 'AIMessageChunk') {
    return null;
  }

  const toolCalls = [...(chunk.tool_calls ?? []), ...(chunk.tool_call_chunks ?? [])];
  if (toolCalls.length > 0) {
    const tools = readToolCalls(toolCalls);
    return tools.length > 0 ? { kind: 'toolCall', tools } : null;
  }

  const text = readTextContent(chunk.content);
  return text.length > 0 ? { kind: 'text', messageId: chunk.id ?? '', text } : null;
}
